import { useNavigate } from 'react-router-dom';

import { useGoogleLogin } from '@react-oauth/google';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { IconBrandGoogle } from '@components/icons/brand-google';
import { IconBrandMicrosoft } from '@components/icons/brand-microsoft';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { config } from 'app-config';

import { useSignupCompany } from 'api/users/hooks';

import { linkProps, SSOErrors, Title } from './common';

import { routePaths } from '../navigation';

export type SignupCompanyFormData = {
  businessEmail: string;
};

const baseRedirectURI = `${config.API_URL}/v1/signup/callback`;

const Form = createForm<SignupCompanyFormData>();

export const SignupCompanyPage = () => {
  const { t, styledTranslate } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const navigate = useNavigate();

  const handleSignupSuccess = () => {
    const email = formStore.getState().values.businessEmail;
    navigate(`/${routePaths.confirmCode}?email=${email}`);
  };

  const signupMutation = useSignupCompany({ onSuccess: handleSignupSuccess });

  const handleContinue = (data: SignupCompanyFormData) => {
    signupMutation.mutate(data);
  };

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: `${baseRedirectURI}/google/`,
  });
  const microsoftURL = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize
    ?client_id=330c6a64-65e6-4f8a-b78e-4a7f610429a7
    &response_type=code
    &redirect_uri=${baseRedirectURI}/microsoft/
    &response_mode=query
    &scope=openid profile email User.Read
  `;

  return (
    <Stack space={6}>
      <Stack align="center">
        <Title>{t('title')}</Title>
        <Text text={t('subtitle')} font="bold18" color="textHelper" />
      </Stack>
      <Stack space={4}>
        <SSOErrors />
        <Stack space={2}>
          <Button
            iconLeft={<IconBrandGoogle size="24" />}
            isFullWidth
            onClick={() => googleLogin()}
            text={t('signupGoogle')}
            variant="secondary"
          />
          <Button
            isFullWidth
            iconLeft={<IconBrandMicrosoft size="24" />}
            href={microsoftURL}
            text={t('signupMicrosoft')}
            variant="secondary"
          />
        </Stack>
      </Stack>
      <Stack
        space={2}
        orientation="horizontal"
        justify="center"
        align="center"
        style={{ width: '100%' }}
      >
        <Separator />
        <Text font="semibold14" color="textSecondary" text={t('or')} caps />
        <Separator />
      </Stack>

      <Form onSubmit={handleContinue} store={formStore}>
        <Stack space={6}>
          <Form.TextField
            id="businessEmail"
            label={t('workEmail')}
            validators={['required', 'business-email']}
            hideRequiredIndicator
          />

          <Form.Submit
            variant="primary-blue"
            text={t('core.continue')}
            isDisabled={signupMutation.isLoading}
          />
          <Stack space={6} align="center">
            <Text
              center
              font="regular14"
              color="textSecondary"
              text={styledTranslate('disclaimer', {
                terms: (
                  <ActionText.ExternalLink {...linkProps} href="https://50skills.com/terms-of-use">
                    {t('terms')}
                  </ActionText.ExternalLink>
                ),
                privacy: (
                  <ActionText.ExternalLink
                    {...linkProps}
                    href="https://50skills.com/privacy-policy"
                  >
                    {t('privacy')}
                  </ActionText.ExternalLink>
                ),
              })}
            />
            <ActionText.Link {...linkProps} underline={false} to={routePaths.signin}>
              {t('existing')}
            </ActionText.Link>
          </Stack>
        </Stack>
      </Form>
    </Stack>
  );
};

const locale = {
  en: {
    title: 'Ease your workload',
    subtitle: 'Create your company account',
    workEmail: 'Work email',
    disclaimer: 'By creating an account, you agree to the {terms} and {privacy}.',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    signupGoogle: 'Sign up with Google',
    signupMicrosoft: 'Sign up with Microsoft',
    or: 'or',
    existing: 'Already have an account? Sign in',
  },
};
